import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const PRIMARY_BROWN_DARK = '#A86A46';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

const CustomerDashboard = () => {
  const { currentUser, upgradeUserRole, userRole, signout } = useAuth();
  const [upgrading, setUpgrading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Check if we're on mobile
  React.useEffect(() => {
    let resizeTimer;
    const checkMobile = () => {
      // Debounce resize events to prevent excessive triggers
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 150);
    };
    
    setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile, { passive: true });
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsDropdownOpen(false);
        }
      } catch (error) {
        // Silently handle errors that might come from browser extensions
        // accessing frameId or other properties on null objects
        if (!error.message?.includes('frameId')) {
          console.error('Error handling click outside:', error);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleUpgradeToCreator = async () => {
    try {
      setUpgrading(true);
      navigate('/onboarding');
    } catch (error) {
      console.error('Failed to navigate to onboarding:', error);
    } finally {
      setUpgrading(false);
    }
  };



  const handleLogout = async () => {
    try {
      await signout();
      navigate('/landing');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Suppress browser extension errors in console and uncaught promise rejections
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Filter out browser extension errors about frameId
      if (args.some(arg => 
        typeof arg === 'string' && (
          arg.includes('frameId') || 
          arg.includes('background.js') ||
          arg.includes('Cannot read properties of null')
        )
      )) {
        // Silently ignore these extension errors
        return;
      }
      originalError.apply(console, args);
    };

    // Catch uncaught promise rejections from browser extensions
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      const errorMessage = error?.message || String(error || '');
      const errorStack = error?.stack || '';
      
      // Suppress browser extension errors
      if (
        errorMessage.includes('frameId') ||
        errorStack.includes('background.js') ||
        errorMessage.includes('Cannot read properties of null')
      ) {
        event.preventDefault(); // Prevent error from showing in console
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.error = originalError;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG,
      paddingBottom: '80px' }}>
      
      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
      
      {/* Header */}
      <header style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 16 : 0,
            padding: isMobile ? '24px 0' : '32px 0',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: isMobile ? 'center' : 'center'
          }}>
            <h1 style={{ 
              ...headingFont, 
              fontSize: isMobile ? 'clamp(28px, 6vw, 40px)' : 40, 
              color: NEUTRAL_TEXT, 
              margin: 0,
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Food Explorer
            </h1>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? 16 : 24,
              position: 'relative'
            }}>
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <span 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  style={{ 
                    fontSize: 14, 
                    color: ACCENT_GRAY, 
                    ...bodyFont,
                    textAlign: isMobile ? 'center' : 'left',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: NEUTRAL_BG,
                      color: NEUTRAL_TEXT
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = NEUTRAL_BG;
                    e.target.style.color = NEUTRAL_TEXT;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = ACCENT_GRAY;
                  }}
                >
                  Welcome, {currentUser?.displayName || 'User'}
                </span>
                
                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: NEUTRAL_WHITE,
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    minWidth: '160px',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: NEUTRAL_TEXT,
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #F3F4F6',
                        ...bodyFont
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = NEUTRAL_BG;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px', paddingBottom: '120px' }}>
        {/* Hero Section */}
        <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20, marginBottom: 32 }}>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <h2 style={{ ...headingFont, fontSize: 32, color: NEUTRAL_TEXT, marginBottom: 16 }}>Discover Amazing Food</h2>
            <p style={{ color: ACCENT_GRAY, fontSize: 16, marginBottom: 24, ...bodyFont }}>
              Explore local food creators and discover unique culinary experiences
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Link
                  to="/fooddiscovery"
                  style={{
                    background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite',
                    color: '#fff',
                    padding: '16px 32px',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-block',
                    fontFamily: 'Montserrat, sans-serif',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(244, 162, 97, 0.3)',
                    transform: 'translateY(0)'
                  }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(244, 162, 97, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(244, 162, 97, 0.3)';
                }}
              >
                Browse Foods
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 32, marginBottom: 48 }}>
          <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20 }}>
            <div style={{ padding: 32 }}>
              <h3 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 8 }}>Recent Orders</h3>
              <p style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 20, ...bodyFont }}>Track your recent food orders</p>
              <Link
                to="/order-history"
                style={{ color: PRIMARY_BROWN, fontWeight: 600, fontSize: 14, textDecoration: 'none', minHeight: 24, display: 'inline-block' }}
              >
                View all orders →
              </Link>
            </div>
          </div>

          <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20 }}>
            <div style={{ padding: 32 }}>
              <h3 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 8 }}>Favorites</h3>
              <p style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 20, ...bodyFont }}>Your saved food items and creators</p>
              <Link
                to="/favorites"
                style={{ color: PRIMARY_BROWN, fontWeight: 600, fontSize: 14, textDecoration: 'none', minHeight: 24, display: 'inline-block' }}
              >
                View favorites →
              </Link>
            </div>
          </div>

          <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20 }}>
            <div style={{ padding: 32 }}>
              <h3 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 8 }}>Profile Settings</h3>
              <p style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 20, ...bodyFont }}>Manage your account and preferences</p>
              <Link
                to="/profile"
                style={{ color: PRIMARY_BROWN, fontWeight: 600, fontSize: 14, textDecoration: 'none', minHeight: 24, display: 'inline-block' }}
              >
                Edit profile →
              </Link>
            </div>
          </div>

          <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20 }}>
            <div style={{ padding: 32 }}>
              <h3 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 8 }}>Messages</h3>
              <p style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 20, ...bodyFont }}>Chat with sellers about your orders</p>
              <Link
                to="/customer-messages"
                style={{ color: PRIMARY_BROWN, fontWeight: 600, fontSize: 14, textDecoration: 'none', minHeight: 24, display: 'inline-block' }}
              >
                View messages →
              </Link>
            </div>
          </div>

          <div style={{ background: NEUTRAL_WHITE, boxShadow: '0 2px 8px #0001', borderRadius: 20 }}>
            <div style={{ padding: 32 }}>
              <h3 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 8 }}>Legal Center</h3>
              <p style={{ color: ACCENT_GRAY, fontSize: 14, marginBottom: 20, ...bodyFont }}>View platform terms, policies, and guidelines</p>
              <Link
                to="/legal"
                style={{ color: PRIMARY_BROWN, fontWeight: 600, fontSize: 14, textDecoration: 'none', minHeight: 24, display: 'inline-block' }}
              >
                View policies →
              </Link>
            </div>
          </div>
        </div>

        {/* Upgrade to Creator Section - Moved to bottom */}
        {userRole !== 'creator' && userRole !== 'admin' && (
          <div style={{ 
            background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease infinite',
            borderRadius: 20, 
            boxShadow: '0 2px 8px #0002', 
            marginBottom: 32 
          }}>
            <div style={{ padding: 32, textAlign: 'center' }}>
              <h3 style={{ ...headingFont, fontSize: 24, color: '#fff', marginBottom: 8 }}>
                Ready to Share Your Culinary Creations?
              </h3>
              <p style={{ color: '#fff', fontSize: 14, marginBottom: 20, ...bodyFont, opacity: 0.9 }}>
                Join our community of food creators and start selling your delicious dishes
              </p>
              <button
                onClick={handleUpgradeToCreator}
                disabled={upgrading}
                style={{
                  background: '#fff',
                  color: PRIMARY_BROWN,
                  padding: '14px 36px',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 700,
                  border: 'none',
                  minWidth: 44,
                  minHeight: 44,
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: upgrading ? 'not-allowed' : 'pointer',
                  opacity: upgrading ? 0.6 : 1,
                }}
              >
                {upgrading ? 'Starting...' : 'Start Selling'}
              </button>
            </div>
          </div>
        )}
      </main>
      <BottomNavigation />
    </div>
  );
};

export default CustomerDashboard; 